import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, Clock, Users, AlertTriangle, 
  Monitor, Calendar, Tag, ChevronLeft, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import api from '../lib/api';
import AIWeeklyReportWidget from '../components/AIWeeklyReportWidget';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.getAnalytics(parseInt(days));
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Process data for charts
  const getDailyVolumeData = () => {
    if (!analytics?.daily_volume) return [];
    return analytics.daily_volume.map(d => ({
      date: d.date.slice(5), // MM-DD format
      tickets: d.count
    }));
  };

  const getHourlyData = () => {
    if (!analytics?.tickets_by_hour) return [];
    // Fill in missing hours with 0
    const hourMap = {};
    for (let i = 0; i < 24; i++) hourMap[i] = 0;
    analytics.tickets_by_hour.forEach(h => { hourMap[h.hour] = h.count; });
    return Object.entries(hourMap).map(([hour, count]) => ({
      hour: `${hour}:00`,
      tickets: count
    }));
  };

  const getAgingData = () => {
    if (!analytics?.ticket_aging) return [];
    const aging = analytics.ticket_aging;
    return [
      { name: '< 1 day', value: aging['1_day'], color: '#10b981' },
      { name: '1-3 days', value: aging['3_days'], color: '#3b82f6' },
      { name: '3-7 days', value: aging['7_days'], color: '#f59e0b' },
      { name: '7-14 days', value: aging['14_days'], color: '#ef4444' },
      { name: '14+ days', value: aging['30_plus'], color: '#7c3aed' },
    ].filter(d => d.value > 0);
  };

  const getDepartmentData = () => {
    if (!analytics?.tickets_by_department) return [];
    return analytics.tickets_by_department.slice(0, 8);
  };

  const getResolutionData = () => {
    if (!analytics?.resolution_time_by_assignee) return [];
    return analytics.resolution_time_by_assignee.map(r => ({
      name: r.assignee?.split(' ')[0] || 'Unknown',
      minutes: Math.round(r.avg_minutes),
      resolved: r.total_resolved
    }));
  };

  // Process tickets per day by assignee for stacked area chart
  const getAssigneeTimelineData = () => {
    if (!analytics?.tickets_per_day_by_assignee) return { data: [], assignees: [] };
    
    const dateMap = {};
    const assigneeSet = new Set();
    
    analytics.tickets_per_day_by_assignee.forEach(item => {
      if (!dateMap[item.date]) dateMap[item.date] = {};
      dateMap[item.date][item.assignee] = item.count;
      assigneeSet.add(item.assignee);
    });
    
    const assignees = Array.from(assigneeSet).slice(0, 6); // Top 6 assignees
    const data = Object.entries(dateMap).map(([date, counts]) => ({
      date: date.slice(5),
      ...assignees.reduce((acc, a) => ({ ...acc, [a]: counts[a] || 0 }), {})
    }));
    
    return { data, assignees };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const { data: assigneeData, assignees } = getAssigneeTimelineData();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="analytics-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-1">
                <ChevronLeft className="h-4 w-4" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-blue-600" />
                <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-36">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchAnalytics}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* AI Weekly Report - Top of Analytics */}
        <AIWeeklyReportWidget />

        {/* Row 1: Ticket Volume & Tickets by Hour */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                Daily Ticket Volume
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={getDailyVolumeData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="tickets" stroke="#3b82f6" fill="#93c5fd" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-600" />
                Tickets by Hour of Day
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getHourlyData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="tickets" fill="#10b981" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Tickets per Day by Assignee */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              Tickets per Day by Assignee
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={assigneeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {assignees.map((assignee, i) => (
                  <Area 
                    key={assignee}
                    type="monotone" 
                    dataKey={assignee} 
                    stackId="1"
                    stroke={COLORS[i % COLORS.length]} 
                    fill={COLORS[i % COLORS.length]}
                    fillOpacity={0.6}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Row 3: Resolution Time, Aging, Department */}
        <div className="grid grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-600" />
                Avg Resolution Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getResolutionData()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={60} />
                  <Tooltip formatter={(v) => `${v} min`} />
                  <Bar dataKey="minutes" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Ticket Backlog/Aging
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={getAgingData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {getAgingData().map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                Tickets by Department
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getDepartmentData()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="department" type="category" tick={{ fontSize: 9 }} width={70} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Row 4: Top Requesters, Keywords, Asset Failures */}
        <div className="grid grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-600" />
                Top Requesters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {analytics?.top_requesters?.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                      <span className="text-sm truncate max-w-[120px]">{r.name}</span>
                    </div>
                    <span className="text-sm font-medium text-indigo-600">{r.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4 text-teal-600" />
                Common Keywords
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                {analytics?.common_keywords?.map((k, i) => (
                  <span 
                    key={i} 
                    className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs"
                    style={{ fontSize: Math.max(10, Math.min(16, 10 + k.count / 5)) }}
                  >
                    {k.word} ({k.count})
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Monitor className="h-4 w-4 text-red-600" />
                Asset Failure Patterns
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.asset_failure_patterns?.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {analytics.asset_failure_patterns.map((a, i) => (
                    <div 
                      key={i} 
                      onClick={() => navigate(`/assets/${a.asset_id}`)}
                      className="flex items-center justify-between p-2 bg-red-50 rounded cursor-pointer hover:bg-red-100"
                    >
                      <span className="text-sm truncate max-w-[140px]">{a.asset_name}</span>
                      <span className="text-sm font-medium text-red-600">{a.ticket_count} tickets</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No patterns detected</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 5: Category & Priority Breakdown */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4 text-purple-600" />
                Tickets by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart 
                  data={Object.entries(
                    (analytics?.category_trends || []).reduce((acc, item) => {
                      acc[item.category] = (acc[item.category] || 0) + item.count;
                      return acc;
                    }, {})
                  ).map(([category, count]) => ({ category, count })).sort((a, b) => b.count - a.count).slice(0, 8)} 
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="category" type="category" tick={{ fontSize: 9 }} width={80} />
                  <Tooltip formatter={(v) => `${v} tickets`} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Tickets by Priority
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const priorityData = Object.entries(
                  (analytics?.priority_trends || []).reduce((acc, item) => {
                    const p = (item.priority || 'unknown').toLowerCase();
                    acc[p] = (acc[p] || 0) + item.count;
                    return acc;
                  }, {})
                ).map(([priority, count]) => ({ 
                  priority: priority.charAt(0).toUpperCase() + priority.slice(1), 
                  count,
                  fill: priority === 'high' ? '#ef4444' : 
                        priority === 'medium' ? '#f59e0b' : 
                        priority === 'low' ? '#10b981' : '#6b7280'
                }));
                
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={priorityData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="count"
                        nameKey="priority"
                        label={({ priority, count }) => `${priority}: ${count}`}
                        labelLine={false}
                      >
                        {priorityData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, name) => [`${v} tickets`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
